import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Download, Search, DollarSign } from 'lucide-react';
import { toast } from '../hooks/use-toast';

export const DriverEarningsModal = ({ isOpen, onClose, earnings, summary }) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredEarnings = earnings.filter(job =>
    job.customer?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    job.service?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleExportCSV = () => {
    const headers = ['Date', 'Customer', 'Service', 'Total Price', 'Commission Rate', 'Commission', 'Net Earnings', 'Tier'];
    const rows = filteredEarnings.map(e => [
      e.date ? new Date(e.date).toLocaleDateString() : 'N/A',
      e.customer,
      e.service,
      `£${e.total_price?.toFixed(2)}`,
      `${e.commission_rate?.toFixed(1)}%`,
      `£${e.commission?.toFixed(2)}`,
      `£${e.net_earnings?.toFixed(2)}`,
      e.membership_tier
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `my-earnings-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    
    toast({ title: 'Export Successful', description: 'Your earnings exported to CSV' });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" style={{ background: 'rgba(15, 23, 42, 0.98)', borderColor: 'rgba(16, 185, 129, 0.3)' }}>
        <DialogHeader>
          <DialogTitle className="text-white text-2xl flex items-center gap-2">
            <DollarSign className="w-6 h-6 text-green-400" />
            My Earnings History ({filteredEarnings.length} Jobs)
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Total Jobs Revenue: £{summary?.total_price?.toFixed(2) || '0.00'} | 
            Commission Paid: £{summary?.total_commission?.toFixed(2) || '0.00'} | 
            Net Earnings: £{summary?.total_earnings?.toFixed(2) || '0.00'}
          </DialogDescription>
        </DialogHeader>

        {/* Filters */}
        <div className="flex gap-4 mb-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by customer or service..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-wheely-navy-900 border-green-500/30 text-white"
              />
            </div>
          </div>
          <Button
            onClick={handleExportCSV}
            className="bg-green-600 hover:bg-green-700"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-green-500/30">
                <th className="text-left p-3 text-gray-300">Date</th>
                <th className="text-left p-3 text-gray-300">Customer</th>
                <th className="text-left p-3 text-gray-300">Service</th>
                <th className="text-left p-3 text-gray-300">Total Price</th>
                <th className="text-left p-3 text-gray-300">Commission Rate</th>
                <th className="text-left p-3 text-gray-300">Commission</th>
                <th className="text-left p-3 text-gray-300">Net Earnings</th>
                <th className="text-left p-3 text-gray-300">Tier</th>
              </tr>
            </thead>
            <tbody>
              {filteredEarnings.map((earning) => (
                <tr key={earning.id} className="border-b border-wheely-navy-700 hover:bg-wheely-navy-900/50">
                  <td className="p-3 text-gray-300 text-sm">
                    {earning.date ? new Date(earning.date).toLocaleDateString() : 'N/A'}
                  </td>
                  <td className="p-3 text-white">{earning.customer}</td>
                  <td className="p-3">
                    <Badge className="bg-wheely-orange/20">{earning.service}</Badge>
                  </td>
                  <td className="p-3 text-white font-semibold">£{earning.total_price?.toFixed(2)}</td>
                  <td className="p-3 text-wheely-orange-light">{earning.commission_rate?.toFixed(1)}%</td>
                  <td className="p-3 text-red-400">-£{earning.commission?.toFixed(2)}</td>
                  <td className="p-3 text-green-400 font-bold">£{earning.net_earnings?.toFixed(2)}</td>
                  <td className="p-3">
                    <Badge
                      style={{
                        background: earning.membership_tier === 'gold' ? '#facc15' :
                                  earning.membership_tier === 'silver' ? '#94a3b8' : '#EB9868'
                      }}
                    >
                      {earning.membership_tier}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredEarnings.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-400">No completed jobs yet</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
